import React, { useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  Box,
  Fade,
  SxProps,
  TextareaAutosize,
  Theme,
  Typography,
} from '@mui/material';

import clsx from 'clsx';

import CheckmarkIcon from '@watershed/icons/components/Checkmark';
import PencilIcon from '@watershed/icons/components/Pencil';
import ViewIcon from '@watershed/icons/components/View';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { getPaletteUtils, mixinSx } from '@watershed/style/styleUtils';

import Button from '../Button';
import CircularProgress from '../CircularProgress';
import CustomReactMarkdown from '../CustomReactMarkdown';
import TextLink from '../TextLink';
import Well from '../Well';

const inputSx: SxProps<Theme> = (theme) => {
  const paletteUtils = getPaletteUtils(theme.palette);
  return {
    '& textarea': {
      ...paletteUtils.textFieldStyles,
      width: '100%',
      resize: 'vertical',
      '&[aria-invalid="true"]': {
        boxShadow: paletteUtils.boxShadowField.error,
      },
    },
  };
};

export default function MarkdownTextFieldWithPreview({
  id,
  name,
  value = '',
  onChange,
  onDidTogglePreview,
  minRows = 5,
  minHeight = 64,
  maxLength,
  disabled = false,
  error = false,
  helperText,
  isPreviewing: isPreviewingInitial,
  defaultToPreview = false,
  placeholder,
  previewBlankslate = t`No text here yet`,
  previewBackground = 'secondary.light',
  isLoading = false,
  autoFocus = false,
  fsUnmask = false,
  previewSx,
  sx,
  onKeyDown,
  onKeyUp,
  ...textareaProps
}: {
  id?: string;
  name?: string;
  // Make this flexible because sometimes this is used with a Formik field and
  // the initial value is undefined, and typing this as `string` was not
  // catching undefined values when used as `<Field
  // as={MarkdownTextFieldWithPreview} />`.
  value?: string;
  onChange: (
    evt: React.ChangeEvent<HTMLTextAreaElement>,
    value: string
  ) => void;
  onDidTogglePreview?: (isPreviewing: boolean) => void;
  minRows?: number;
  minHeight?: number;
  maxLength?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: LocalizedString;
  /**
   * Allows controlling the preview state from outside the component.
   */
  isPreviewing?: boolean;
  defaultToPreview?: boolean;
  placeholder?: LocalizedString;
  previewBlankslate?: LocalizedString;
  previewBackground?: 'background.paper' | 'secondary.light';
  fsUnmask?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  previewSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
} & Omit<
  React.ComponentProps<typeof TextareaAutosize>,
  'onChange' | 'onKeyDown' | 'onKeyUp'
>) {
  const [isPreviewing, setIsPreviewing] = useState(
    isPreviewingInitial ?? defaultToPreview
  );

  // Allows outside state to control preview state if needed
  useEffect(() => {
    if (
      isPreviewingInitial !== undefined &&
      isPreviewing !== isPreviewingInitial
    ) {
      setIsPreviewing(isPreviewingInitial);
      onDidTogglePreview?.(isPreviewingInitial);
    }
  }, [isPreviewingInitial, isPreviewing, onDidTogglePreview]);

  const togglePreview = () => {
    setIsPreviewing((value) => !value);
    onDidTogglePreview?.(!isPreviewing);
  };

  return (
    <Box width="100%" sx={{ position: 'relative', minHeight }}>
      {isPreviewing ? (
        <>
          <Well
            data-testid={TestIds.MarkdownTextFieldWithPreviewDisplay}
            onDoubleClick={
              isPreviewingInitial === undefined ? togglePreview : undefined
            }
            sx={mixinSx(
              {
                paddingY: 1,
                paddingX: 2,
                minHeight: 36,
                marginBottom: 0.5,
                backgroundColor: previewBackground,
              },
              previewSx
            )}
          >
            {value.length > 0 ? (
              <CustomReactMarkdown source={value} />
            ) : (
              <Typography variant="body2">{previewBlankslate}</Typography>
            )}
          </Well>

          {helperText && (
            <Typography sx={{ color: (theme) => theme.palette.error.main }}>
              {helperText}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Box sx={mixinSx(inputSx, sx)}>
            <TextareaAutosize
              data-testid={TestIds.MarkdownTextFieldWithPreviewTextArea}
              autoFocus={autoFocus}
              id={id}
              name={name}
              value={value}
              minRows={minRows}
              maxLength={maxLength}
              aria-invalid={error}
              disabled={disabled}
              placeholder={placeholder ?? previewBlankslate}
              onChange={(evt: React.ChangeEvent<HTMLTextAreaElement>) =>
                onChange(evt, evt.currentTarget.value)
              }
              className={clsx(textareaProps.className, fsUnmask && 'fs-unmask')}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              {...textareaProps}
            />
          </Box>
          {helperText && (
            <Typography sx={{ color: (theme) => theme.palette.error.main }}>
              {helperText}
            </Typography>
          )}
        </>
      )}
      {!disabled && (
        <Box display="flex" alignItems="center" width="100%">
          {!isPreviewing && (
            <Typography variant="caption">
              <Trans context="Caption that indicates a text field will support markdown">
                Supports{' '}
                <TextLink
                  href="https://www.markdownguide.org/basic-syntax/"
                  target="_blank"
                  rel="noreferrer"
                  color="inherit"
                  underline="always"
                >
                  Markdown
                </TextLink>
              </Trans>
            </Typography>
          )}
          <Box flexGrow={1} />
          {isLoading && (
            <Fade in>
              <CircularProgress size={16} color="primary" />
            </Fade>
          )}
          {isPreviewingInitial === undefined && (
            <Button
              onClick={togglePreview}
              disabled={disabled}
              startIcon={
                isPreviewing ? (
                  <PencilIcon />
                ) : defaultToPreview ? (
                  <CheckmarkIcon />
                ) : (
                  <ViewIcon />
                )
              }
              variant="text"
              size="small"
              data-testid={TestIds.MarkdownTextFieldWithPreviewToggle}
            >
              {isPreviewing ? (
                <Trans context="Edit text markdown">Edit</Trans>
              ) : defaultToPreview ? (
                <Trans context="Done editing text markdown">Done</Trans>
              ) : (
                <Trans context="Preview text markdown">Preview</Trans>
              )}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
